import { createTimeInstance } from '@utils/time';

// Centralized human labels + formatters for the Score Backfill feature. Every surface that
// shows a status, scope, index code, spec id, count, cost, or date routes through here so the
// same machine value never renders two different ways across screens (see #1 in the UX plan).

const EM_DASH = '—';

// Title-case an unknown snake_case / spaced token so we never echo raw machine strings.
const humanizeToken = (value) =>
  String(value)
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');

// `canceling` is a display-only status — the API never sends it. See deriveDisplayStatus.
export const STATUS_LABELS = {
  queued: 'Queued',
  running: 'Running',
  completed: 'Completed',
  failed: 'Failed',
  canceled: 'Canceled',
  canceling: 'Canceling…',
};

// A cancel is cooperative: the job carries cancel_requested_at while status is still `running`
// until the worker finalizes it. Every surface routes its status through here so that window
// renders as "Canceling…" rather than a still-Running badge. Deliberately matched against the
// non-terminal statuses (see BACKFILL_TERMINAL_STATUSES) so a cancel that lost the race to a
// finishing run doesn't relabel an already-completed job — and so this stays a leaf display
// module with no dependency on the service layer.
const CANCELABLE_STATUSES = ['queued', 'running'];

export const deriveDisplayStatus = (job) => {
  if (!job) return null;
  if (job.cancel_requested_at && CANCELABLE_STATUSES.includes(job.status)) return 'canceling';
  return job.status;
};

export const SCOPE_LABELS = {
  full_history: 'Full history',
  latest_per_company: 'Latest per company',
};

export const UNIVERSE_KIND_LABELS = {
  whole_corpus: 'Whole corpus',
  index: 'Index',
  tickers: 'Tickers',
};

// Best-effort pretty names for known index codes; unknown codes fall back to upper-case.
export const INDEX_LABELS = {
  sp500: 'S&P 500',
  sp400: 'S&P 400',
  sp600: 'S&P 600',
  sp1500: 'S&P 1500',
  russell1000: 'Russell 1000',
  russell2000: 'Russell 2000',
  russell3000: 'Russell 3000',
  nasdaq100: 'Nasdaq 100',
  dow30: 'Dow 30',
};

export const formatStatus = (status) => STATUS_LABELS[status] || (status ? humanizeToken(status) : EM_DASH);

export const formatScope = (scope) => SCOPE_LABELS[scope] || (scope ? humanizeToken(scope) : EM_DASH);

export const formatUniverseKind = (kind) => UNIVERSE_KIND_LABELS[kind] || (kind ? humanizeToken(kind) : EM_DASH);

export const formatIndex = (code) => {
  if (!code) return EM_DASH;
  const key = String(code).toLowerCase();
  return INDEX_LABELS[key] || String(code).toUpperCase();
};

// 'forward_dividend_likelihood_v1' -> 'Forward Dividend Likelihood (v1)'
export const formatSpecId = (id) => {
  if (!id) return EM_DASH;
  const parts = String(id).split(/[_\s]+/).filter(Boolean);
  const rendered = parts.map((part) => {
    if (/^v\d+$/i.test(part)) return `(${part.toLowerCase()})`;
    return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
  });
  return rendered.join(' ').replace(/\s+\(/g, ' (');
};

export const formatCount = (n) => (typeof n === 'number' && isFinite(n) ? n.toLocaleString() : EM_DASH);

export const formatUsd = (n) =>
  typeof n === 'number' && isFinite(n)
    ? `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : EM_DASH;

export const formatDate = (d) => (d ? createTimeInstance(d).format('MMM D, YYYY') : EM_DASH);

export const formatWindow = (from, to) => `${formatDate(from)} → ${formatDate(to)}`;

// Since ai_copilot#592 (prod 2026-09-30) a backfill's from/to bound each occasion's utc_knowable_at
// — its trigger time, read as UTC days, for every spec kind: the same column the feed's knowable
// window bounds. Before the switch the same two dates bounded release dates (calendar specs) or the
// trigger document's publish date. The request and the stored fields never changed, only their
// meaning, so the API says which on `window_bounds`: 'utc_knowable_at', or null for a job created
// before the switch. Only an explicit null is a legacy window — a job with no `window_bounds` key
// at all came from an API that predates the field and says nothing either way.
export const isLegacyWindow = (job) => !!job && job.window_bounds === null;

// The two phrasings every surface that shows a legacy job's dates draws from, so History and
// Progress cannot describe the same run two ways. The short one fits a table cell; the long one says
// what the dates bounded, and why the same dates on a new run would select differently.
export const LEGACY_WINDOW_LABEL = 'Document dates';
export const LEGACY_WINDOW_NOTE =
  'This run’s window bounded document dates. Runs since Sep 30, 2026 bound when occasions became knowable, so the same dates may select different occasions.';

export const formatScored = (completed, total) => `${formatCount(completed)} / ${formatCount(total)}`;

// Prefer an explicit human name; otherwise render the score_spec_id as a clean title
// (e.g. "forward_buyback_intent_5_mini_v1" -> "Forward Buyback Intent 5 Mini (v1)"). The
// description is intentionally not used as the label — it's sentence-length prose, not a name.
export const specDisplayName = (spec) => {
  if (!spec) return EM_DASH;
  return spec.name || formatSpecId(spec.score_spec_id);
};

export const specLabelById = (specs, specId) => {
  const match = (specs || []).find((s) => s.score_spec_id === specId);
  return match ? specDisplayName(match) : formatSpecId(specId);
};
